import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import type { UserMembership } from '../types/data-models';

interface TenantContextValue {
  /** Currently active tenant id (from the JWT). */
  activeTenantId: number | null;
  activeTenantName: string | null;
  memberships: UserMembership[];
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

export function TenantProvider({ children }: { children: ReactNode }) {
  const { user, memberships } = useAuth();

  const value = useMemo<TenantContextValue>(() => {
    const activeTenantId = user?.tenantId ?? null;
    const active = memberships.find((m) => m.tenantId === activeTenantId);
    return {
      activeTenantId,
      activeTenantName: active?.tenantName ?? null,
      memberships,
    };
  }, [user, memberships]);

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used inside TenantProvider');
  return ctx;
}
